import { useMemo, type ReactNode } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { CarIcon, UserCircleIcon } from '@phosphor-icons/react';
import type { DirectoryItemInfoField } from '@/features/DirectoryItemPage';
import { cn } from '@/lib/utils';
import { Badge } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import { getEventEntitiesCacheKey } from '@/constants/cache-keys';
import { AppRoutes } from '@/constants/routes';
import type { EventEntities } from '@/types/event';
import { getEntityKindForClass } from '@/utils/detection-classes';
import { buildEntityAnalyticsFields } from '@/features/DirectoryItemPage/entityAnalyticsFields';
import { UpdatePersonModal } from '@/features/PersonGrid/components/UpdatePersonModal';
import { AddVehicleModal } from '@/features/VehicleGrid/components/AddVehicleModal';
import { useEventEntityLink } from '../hooks/useEventEntityLink';
import { useEventEntityEditors } from '../hooks/useEventEntityEditors';
import { EventEntityCard } from './EventEntityCard';
import { EventEntityMenu } from './EventEntityMenu';
import { LinkEntityPicker } from './LinkEntityPicker';

export interface EventEntity {
    id: string;
    icon: ReactNode;
    imageUrl?: string;
    name: string;
    badge?: ReactNode;
    tags?: string[];
    fields: DirectoryItemInfoField[];
    href?: string;
    actions?: ReactNode;
}

interface EventEntitiesGridProps {
    eventId: string;
    returnUrl?: string;
    classId?: number;
    showActions?: boolean;
}

export function EventEntitiesGrid({
    eventId,
    returnUrl,
    classId,
    showActions = false,
}: EventEntitiesGridProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const { t: tDirectory } = useTranslation(I18nNamespace.DIRECTORY);

    const { data: entitiesData, mutate } = useSWR<EventEntities>(
        eventId ? getEventEntitiesCacheKey(eventId) : null,
        () => EngineService.getEventEntities(eventId),
    );

    const entityKind = getEntityKindForClass(classId);
    const { unlink, link } = useEventEntityLink({
        eventId,
        entities: entitiesData,
        entityKind,
    });

    const {
        editPerson,
        editVehicle,
        editPersonId,
        editVehicleId,
        setEditPersonId,
        setEditVehicleId,
        handleUpdatePerson,
        handleUpdateVehicle,
        duplicatePlateDialog,
    } = useEventEntityEditors(mutate);

    const entities: EventEntity[] = useMemo(() => {
        if (!entitiesData) return [];

        const result: EventEntity[] = [];

        entitiesData.vehicles.forEach((entry) => {
            result.push({
                id: entry.vehicle.id,
                href: generatePath(AppRoutes.VEHICLE, {
                    vehicleId: entry.vehicle.id,
                }),
                icon: <CarIcon className="text-grey-300" weight="regular" />,
                imageUrl: entry.vehicle.thumbnailUrl,
                name:
                    entry.vehicle.name ||
                    tDirectory('vehicles.card.unknownVehicle'),
                badge: (
                    <Badge
                        variant="license-plate"
                        value={entry.licensePlateText}
                    />
                ),
                tags: entry.vehicle.tags,
                fields: buildEntityAnalyticsFields(tDirectory, {
                    firstSeen: entry.vehicle.createdAt,
                    lastSeen: entry.lastSeen,
                    totalEvents: entry.totalEvents,
                    totalSources: entry.totalSources,
                }),
                actions:
                    showActions && entityKind === 'vehicle' ? (
                        <EventEntityMenu
                            isUnknown={!entry.vehicle.known}
                            replaceContent={(onClose) => (
                                <LinkEntityPicker
                                    entityKind="vehicle"
                                    sourceEntityId={entry.vehicle.id}
                                    onLink={(id) => void link(id)}
                                    onClose={onClose}
                                />
                            )}
                            onUnlink={() => void unlink()}
                            onEdit={() => setEditVehicleId(entry.vehicle.id)}
                        />
                    ) : undefined,
            });
        });

        entitiesData.persons.forEach((entry) => {
            result.push({
                id: entry.person.id,
                href: generatePath(AppRoutes.PERSON, {
                    personId: entry.person.id,
                }),
                icon: (
                    <UserCircleIcon
                        className="text-grey-300"
                        weight="regular"
                    />
                ),
                imageUrl: entry.person.imageUrl,
                name:
                    entry.person.name ||
                    t('eventView.linkEntity.unknownPerson'),
                fields: buildEntityAnalyticsFields(tDirectory, {
                    firstSeen: entry.person.createdAt,
                    lastSeen: entry.lastSeen,
                    totalEvents: entry.totalEvents,
                    totalSources: entry.totalSources,
                }),
                actions:
                    showActions && entityKind === 'person' ? (
                        <EventEntityMenu
                            isUnknown={!entry.person.name}
                            replaceContent={(onClose) => (
                                <LinkEntityPicker
                                    entityKind="person"
                                    sourceEntityId={entry.person.id}
                                    onLink={(id) => void link(id)}
                                    onClose={onClose}
                                />
                            )}
                            onUnlink={() => void unlink()}
                            onEdit={() => setEditPersonId(entry.person.id)}
                        />
                    ) : undefined,
            });
        });

        return result;
    }, [entitiesData, t, tDirectory, showActions, entityKind, unlink, link]);

    if (entities.length === 0) return null;

    return (
        <div className="flex flex-col gap-2 mt-2 text-sm">
            <span className="text-sm-medium text-grey-200">
                {t('infoPanel.associatedIdentities')}
            </span>
            <div
                className={cn(
                    'grid gap-4',
                    entities.length === 1
                        ? 'grid-cols-1'
                        : 'grid-cols-1 @narrow:grid-cols-2',
                )}
            >
                {entities.map((entity) => (
                    <EventEntityCard
                        key={entity.id}
                        icon={entity.icon}
                        imageUrl={entity.imageUrl}
                        name={entity.name}
                        badge={entity.badge}
                        tags={entity.tags}
                        fields={entity.fields}
                        href={entity.href}
                        returnUrl={returnUrl}
                        actions={entity.actions}
                    />
                ))}
            </div>
            {editPerson && (
                <UpdatePersonModal
                    isOpen={Boolean(editPersonId)}
                    onClose={() => setEditPersonId(null)}
                    mode="edit"
                    person={editPerson}
                    onUpdate={handleUpdatePerson}
                />
            )}
            {editVehicle && (
                <AddVehicleModal
                    isOpen={Boolean(editVehicleId)}
                    onClose={() => setEditVehicleId(null)}
                    mode="edit"
                    vehicle={editVehicle}
                    onUpdate={handleUpdateVehicle}
                />
            )}
            {duplicatePlateDialog}
        </div>
    );
}
